import { useDispatch, useSelector } from "react-redux";
import { SurveyorSearch } from "./SurveyorSearch";
import { VendorSearch } from "./VendorSearch";
import { LoggedUserType } from "../../../../models/GlobalModels";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useMemo, useState } from "react";
import { handleGetIcvDataSlice, handleICVGetInfoDataSlice } from "../../../../redux/slices/icvActivitySlice";
import { handleICVActivitiesPaginationDataSlice, handleUpdatePageSlice } from "../../../../redux/slices/globalSlice";
import { handleCHMGisChartDataSlice, handleCHMInfoByIdDataSlice } from "../../../../redux/slices/chmActivitySlice";
import ToolBar from "../../../../components/ToolBar";
import NoPermission from "../../../../NoAccess/NoPermission";
import { Tab, Tabs } from "@mui/material";
import { loginSelector } from "../../../../redux/slices/loginSlice";
import './VMScreation.css'


interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <div className="pt-3 tab-container">{children}</div>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

const VMS = () => {
  const allTabOptions = [
    {
      label: "Vendor Masters",
      tabId: 0,
      permission: "Completed-ICV-Tab",
      component: VendorSearch,
    },
    {
      label: "Surveyor Masters",
      tabId: 1,
      permission: "Completed-ICV-Tab",
      component: SurveyorSearch,
    },
  ];

    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    const { storeICVActivitiesPaginationData } = useSelector((state: any) => state.global);
    const user: LoggedUserType = useSelector(loginSelector).user;

    const authorizedTabs = useMemo(() => {
        return allTabOptions
            .filter(tab => user?.permissions?.includes(tab.permission))
            .map((tab, index) => ({
                ...tab,
                displayIndex: index 
            }));
    }, [user?.permissions]);

    const [value, setValue] = useState<number>(() => {
        const savedValue = localStorage.getItem("selectedVMSTab");
        if (savedValue !== null) {
            const savedTabId = parseInt(savedValue, 10);
            // Find if the saved tab is still authorized
            const authorizedTab = authorizedTabs.find(tab => tab.tabId === savedTabId);
            if (authorizedTab) {
                return authorizedTab.displayIndex;
            }
        }
        // Default to first authorized tab
        return authorizedTabs.length > 0 ? 0 : 0;
    });

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        // Save the actual tabId, not the display index
        const selectedTab = authorizedTabs[newValue];
        if (selectedTab) {
            localStorage.setItem("selectedVMSTab", selectedTab.tabId.toString());
        }
        
        // Conditionally clear VMS data based on tab navigation
        if (selectedTab?.tabId === 1) {
            // ICV Activities tab - clear ICV data
            dispatch(handleGetIcvDataSlice({ data: [], count: 0 }));
        } else if (selectedTab?.tabId === 2) {
            // Completed ICV tab - clear ICV info data
            dispatch(handleICVGetInfoDataSlice({ data: [], count: 0 }));
        }
        
        dispatch(handleUpdatePageSlice(false));
        dispatch(handleCHMInfoByIdDataSlice({}));
        dispatch(handleCHMGisChartDataSlice({}));
        dispatch(
            handleICVActivitiesPaginationDataSlice({
                ...storeICVActivitiesPaginationData,
                offset: 0,
                limit: 250,
                selectedLimit: 250,
            })
        );
    };

    // If no tabs are authorized, show a message
    if (authorizedTabs.length === 0) {
        return (
          <>
            <div style={{ position: "sticky", top: "0", zIndex: "1000" }}>
              <ToolBar
                title={"Vendor and Surveyor Management (ICV)"}
                className={"rounded-toolbar"}
              />
            </div>
            <div className="pt-3">
              <NoPermission />
            </div>
          </>
        );
    }

    return (
      <>
        <div style={{ position: "sticky", top: "0", zIndex: "1000" }}>
          <ToolBar title={"Vendor and Surveyor Management (ICV)"} />

          <div className="tab-section">
            <Tabs
              TabIndicatorProps={{
                style: { background: "rgba(92, 121, 170, 1)" },
              }}
              variant="fullWidth"
              value={value}
              onChange={handleChange}
            >
              {authorizedTabs.map((tab) => (
                <Tab
                  key={tab.tabId}
                  label={tab.label}
                  style={{
                    color: "rgba(92, 121, 170, 1)",
                    fontWeight: "600",
                    textTransform: "none",
                  }}
                  {...a11yProps(tab.displayIndex)}
                />
              ))}
            </Tabs>
          </div>
        </div>

        {/* Render panels with correct display indices */}
        {authorizedTabs.map((tab) => {
          const Component = tab.component;
          return (
            <CustomTabPanel
              key={tab.tabId}
              value={value}
              index={tab.displayIndex}
            >
              <Component />
            </CustomTabPanel>
          );
        })}
      </>
    );
}

export default VMS;